import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Link2, Check, GripVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import MediaUrlsInput, { absolutize } from '../../components/MediaUrlsInput';
import TranslationsEditor from '../../components/TranslationsEditor';
import BulkTranslateButton from '../../components/BulkTranslateButton';
import DefaultsControls from '../../components/DefaultsControls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const CuratedDestinationsManager = () => {
  const [destinations, setDestinations] = useState([]);
  const [partnerPackages, setPartnerPackages] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [editingDest, setEditingDest] = useState(null);
  const [linkingDest, setLinkingDest] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image: '',
    media: [],
    vibe: '',
    description: '',
    facts: [{ label: '', value: '' }],
    linked_package_ids: [],
    sort_order: 0,
    translations: {}
  });

  const fetchData = async () => {
    try {
      const [destRes, pkgRes] = await Promise.all([
        axios.get(`${API}/curated-destinations`),
        axios.get(`${API}/partner-packages`)
      ]);
      setDestinations(destRes.data);
      setPartnerPackages(pkgRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '', image: '', media: [], vibe: '', description: '',
      facts: [{ label: '', value: '' }], linked_package_ids: [], sort_order: 0,
      translations: {}
    });
    setEditingDest(null);
  };

  const openCreate = () => {
    resetForm();
    setFormData(f => ({ ...f, sort_order: destinations.length + 1 }));
    setIsDialogOpen(true);
  };

  const openEdit = (dest) => {
    const media = (dest.media && dest.media.length > 0)
      ? dest.media
      : (dest.image ? [dest.image] : []);
    setFormData({
      title: dest.title || '',
      image: dest.image || (media[0] || ''),
      media,
      vibe: dest.vibe || '',
      description: dest.description || '',
      facts: dest.facts && dest.facts.length > 0 ? dest.facts : [{ label: '', value: '' }],
      linked_package_ids: dest.linked_package_ids || [],
      sort_order: dest.sort_order || 0,
      translations: dest.translations || {}
    });
    setEditingDest(dest);
    setIsDialogOpen(true);
  };

  const openLinkDialog = (dest) => {
    setLinkingDest(dest);
    setIsLinkDialogOpen(true);
  };

  const togglePackageLink = async (pkgId) => {
    if (!linkingDest) return;
    const token = localStorage.getItem('token');
    const currentLinks = linkingDest.linked_package_ids || [];
    const newLinks = currentLinks.includes(pkgId)
      ? currentLinks.filter(id => id !== pkgId)
      : [...currentLinks, pkgId];

    try {
      await axios.put(`${API}/curated-destinations/${linkingDest.id}`, {
        title: linkingDest.title,
        image: linkingDest.image,
        media: linkingDest.media || [],
        vibe: linkingDest.vibe,
        description: linkingDest.description,
        facts: linkingDest.facts,
        linked_package_ids: newLinks,
        sort_order: linkingDest.sort_order,
        translations: linkingDest.translations || {}
      }, { headers: { Authorization: `Bearer ${token}` } });

      setLinkingDest({ ...linkingDest, linked_package_ids: newLinks });
      fetchData();
      toast.success('Links updated');
    } catch (error) {
      toast.error('Failed to update links');
    }
  };

  const addFact = () => {
    setFormData({ ...formData, facts: [...formData.facts, { label: '', value: '' }] });
  };

  const removeFact = (idx) => {
    setFormData({ ...formData, facts: formData.facts.filter((_, i) => i !== idx) });
  };

  const updateFact = (idx, field, val) => {
    const updated = [...formData.facts];
    updated[idx] = { ...updated[idx], [field]: val };
    setFormData({ ...formData, facts: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (!formData.media || formData.media.length === 0) {
      toast.error('Please add at least one image or video.');
      return;
    }

    const payload = {
      ...formData,
      // First media item is the cover image; keep `image` in sync for backwards compat.
      image: formData.media[0],
      facts: formData.facts.filter(f => f.label && f.value)
    };

    try {
      if (editingDest) {
        await axios.put(`${API}/curated-destinations/${editingDest.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Destination updated');
      } else {
        await axios.post(`${API}/curated-destinations`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Destination created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save destination');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this destination?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/curated-destinations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Destination deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getLinkedCount = (dest) => (dest.linked_package_ids || []).length;

  return (
    <div data-testid="curated-destinations-manager">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-heading font-bold text-maroon">Destinations</h2>
          <p className="text-maroon/60 text-sm mt-1">Manage destination cards and link them to partner packages</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DefaultsControls
            section="destinations"
            reseedConfirm={
              'Re-seed default destinations from your last saved snapshot?\n\n' +
              'Replaces every destination whose title is in the snapshot. If no snapshot ' +
              'has been saved yet, falls back to the original 10 sustainable-travel cards ' +
              '(Costa Rica, Belize, Panama, Guatemala, Peru, Colombia, Mexico, Uruguay, Argentina, Chile).'
            }
            onComplete={fetchData}
            reseedTestId="reseed-destinations-btn"
            snapshotTestId="snapshot-destinations-btn"
          />
          <BulkTranslateButton scope="curated-destinations" onComplete={fetchData} />
          <Button onClick={openCreate} className="btn-primary" data-testid="add-destination-btn">
            <Plus size={20} className="mr-2" />
            Add Destination
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {destinations.map((dest, idx) => (
          <div key={dest.id} className="card flex items-center gap-4" data-testid="curated-dest-item">
            <div className="flex items-center text-maroon/30">
              <GripVertical size={20} />
              <span className="text-xs font-bold ml-1">#{dest.sort_order}</span>
            </div>
            <img src={absolutize(dest.image)} alt={dest.title} className="w-24 h-16 object-cover rounded-sm border border-maroon/10 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-bold text-lg text-maroon">{dest.title}</h3>
              <p className="text-maroon/60 text-sm truncate">{dest.vibe}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs bg-ocean/10 text-ocean px-2 py-0.5 rounded-sm font-semibold">
                  {getLinkedCount(dest)} package{getLinkedCount(dest) !== 1 ? 's' : ''} linked
                </span>
                {dest.facts && dest.facts.map((f, i) => (
                  <span key={i} className="text-xs text-maroon/40">{f.label}: {f.value}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={() => openLinkDialog(dest)}
                variant="outline"
                size="sm"
                className="border-ocean/30 text-ocean hover:bg-ocean/5"
                data-testid="link-packages-btn"
              >
                <Link2 size={16} className="mr-1" />
                Link
              </Button>
              <Button
                onClick={() => openEdit(dest)}
                variant="outline"
                size="sm"
                className="border-maroon/20 text-maroon hover:bg-maroon/5"
                data-testid="edit-dest-btn"
              >
                Edit
              </Button>
              <Button
                onClick={() => handleDelete(dest.id)}
                variant="destructive"
                size="sm"
                data-testid="delete-dest-btn"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-maroon">
              {editingDest ? 'Edit Destination' : 'Add Destination'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="dest-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="input-field" placeholder="e.g., Mexico" />
              </div>
              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} className="input-field" />
              </div>
            </div>

            <MediaUrlsInput
              urls={formData.media}
              onChange={(media) => setFormData({ ...formData, media })}
            />

            <div className="space-y-2">
              <Label>Vibe (short tagline) *</Label>
              <Input value={formData.vibe} onChange={(e) => setFormData({ ...formData, vibe: e.target.value })} required className="input-field" placeholder="A short evocative 1-2 line description..." />
            </div>

            <div className="space-y-2">
              <Label>Full Description *</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={4} className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none" placeholder="Longer paragraph about the destination..." />
            </div>

            {/* Quick Facts */}
            <div className="space-y-2">
              <Label>Quick Facts</Label>
              {formData.facts.map((fact, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    value={fact.label}
                    onChange={(e) => updateFact(idx, 'label', e.target.value)}
                    placeholder="Label (e.g., Best for)"
                    className="input-field flex-1"
                  />
                  <Input
                    value={fact.value}
                    onChange={(e) => updateFact(idx, 'value', e.target.value)}
                    placeholder="Value (e.g., Romance)"
                    className="input-field flex-1"
                  />
                  <button type="button" onClick={() => removeFact(idx)} className="p-2 text-coral hover:bg-coral/10 rounded-sm">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addFact} className="border-maroon/20">
                <Plus size={14} className="mr-1" /> Add Fact
              </Button>
            </div>

            <TranslationsEditor
              translations={formData.translations}
              onChange={(translations) => setFormData({ ...formData, translations })}
              sourceContent={{
                title: formData.title,
                vibe: formData.vibe,
                description: formData.description,
                facts: formData.facts.filter((f) => f.label && f.value),
              }}
              fields={[
                { key: 'title', label: 'Title' },
                { key: 'vibe', label: 'Vibe / Tagline', type: 'textarea' },
                { key: 'description', label: 'Description', type: 'textarea' },
              ]}
              languages={['es']}
            />

            <Button type="submit" className="btn-primary w-full">
              {editingDest ? 'Update Destination' : 'Create Destination'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Link Packages Dialog */}
      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-maroon">
              Link Packages to {linkingDest?.title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-maroon/60 mb-4">
            Select which partner packages appear when users explore this destination.
          </p>
          {partnerPackages.length === 0 ? (
            <p className="text-maroon/50 text-center py-6">No partner packages created yet. Add some in the Partner Packages section first.</p>
          ) : (
            <div className="space-y-2">
              {partnerPackages.map((pkg) => {
                const isLinked = (linkingDest?.linked_package_ids || []).includes(pkg.id);
                return (
                  <div
                    key={pkg.id}
                    onClick={() => togglePackageLink(pkg.id)}
                    className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
                      isLinked
                        ? 'border-ocean bg-ocean/5'
                        : 'border-maroon/10 hover:border-maroon/30'
                    }`}
                    data-testid={`link-pkg-${pkg.id}`}
                  >
                    <div className={`w-6 h-6 rounded-sm border-2 flex items-center justify-center flex-shrink-0 ${
                      isLinked ? 'bg-ocean border-ocean' : 'border-maroon/20'
                    }`}>
                      {isLinked && <Check size={14} className="text-white" />}
                    </div>
                    {pkg.image && (
                      <img src={pkg.image} alt="" className="w-12 h-12 object-cover rounded-sm flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-terracotta font-semibold uppercase">{pkg.supplier}</p>
                      <p className="font-semibold text-maroon text-sm truncate">{pkg.title}</p>
                      <p className="text-xs text-maroon/50">{pkg.destination} &middot; {pkg.price}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CuratedDestinationsManager;
